import React from 'react'
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import '@inovua/reactdatagrid-community/theme/default-dark.css'
import useApi, {CONTROLLER} from "../../hooks/useApi";
import useStorage from "./useStorage";
import {StoreContext} from '../../store';
import {parseReportResponse} from './reportResponse';

const getHeaderName = (name) => (name || '').replace(/([A-Z]+)/g, ' $1').trim();

const getHeaderTooltipText = (column, fallbackName) => {
  if (typeof column?.header === 'string' && column.header.trim()) {
    return column.header;
  }
  if (fallbackName) {
    return fallbackName;
  }
  return getHeaderName(column?.name);
};

const normalizeSingleSortInfo = (sortInfo) => {
  if (!sortInfo) {
    return sortInfo;
  }
  if (Array.isArray(sortInfo)) {
    return sortInfo[0] || null;
  }
  return sortInfo;
};

const Grid = React.forwardRef((props, ref) => {
  const {loading, execute} = useApi(CONTROLLER.REPORTS);
  const {store} = React.useContext(StoreContext);
  const [reportError, setReportError] = React.useState(null);
  const [retry, setRetry] = React.useState(0);
  const reportScope = JSON.stringify([store.username, store.isAuth, store.paid, store.advanced, props.report]);
  const reportState = React.useRef({scope: reportScope, sequence: 0, good: null});
  if (reportState.current.scope !== reportScope) {
    reportState.current = {scope: reportScope, sequence: 0, good: null};
  }
  const {
    afterLoad,
    dataSource: inputDataSource,
    prepareData,
    report,
    reportParams,
  } = props;
  const isResizable = props.resizable !== false;
  const hasPreference = Boolean(props.preference);
  const isRemoteGrid = Boolean(
    report ||
    props.remoteSort ||
    props.remoteFilter ||
    props.pagination === 'remote' ||
    typeof inputDataSource === 'function',
  );
  const useControlledSortFilter = hasPreference && isRemoteGrid;
  const manualSortOnHeader = React.useMemo(
    () => Boolean(useControlledSortFilter && props.sortable && props.preference === 'dataSearch'),
    [useControlledSortFilter, props.sortable, props.preference],
  );
  const effectiveMultiSort = React.useMemo(
    () => (isRemoteGrid ? (props.multiSort ?? true) : false),
    [isRemoteGrid, props.multiSort],
  );

  // Ensure a consistent grid typography across the site (Top 50 table look).
  // Keep any caller-provided className intact.
  const mergedClassName = React.useMemo(() => {
    const base = 'dt-grid';
    if (!props.className) {
      return base;
    }
    if (String(props.className).includes(base)) {
      return props.className;
    }
    return `${base} ${props.className}`;
  }, [props.className]);

  const defColumns = React.useMemo(
    () => props.columns.map((column) => {
      if (typeof column === 'string') {
        const header = getHeaderName(column);
        return {
          name: column,
          header,
          defaultFlex: 1,
          headerAlign: 'center',
          ...(props.sortable ? { sortable: true } : {}),
          headerDOMProps: {
            title: header,
          },
        };
      }

      const defaultHeader = getHeaderName(column.name);
      const merged = {
        header: defaultHeader,
        defaultFlex: 1,
        headerAlign: 'center',
        ...column,
      };
      if (props.sortable && typeof merged.sortable === 'undefined') {
        merged.sortable = true;
      }
      const title = getHeaderTooltipText(merged, defaultHeader);
      const headerDOMProps = merged.headerDOMProps || {};
      return {
        ...merged,
        headerDOMProps: headerDOMProps.title ? headerDOMProps : { ...headerDOMProps, title },
      };
    }),
    [props.columns, props.sortable],
  );

  const {
    columns,
    sortInfo,
    columnOrder,
    filterValue,
    initialSortInfo,
    initialFilterValue,
    preferenceSignature,
    columnPreferenceSignature,
    filterConflict,
    onSortInfoChange,
    onColumnResize,
    onColumnOrderChange,
    onFilterValueChange,
    onColumnVisibleChange,
  } = useStorage(
    ref,
    props.preference,
    {
      columns: defColumns,
      defaultSortInfo: props.defaultSortInfo,
      defaultFilterValue: props.defaultFilterValue,
      manualSortOnHeader,
    },
  );

  const gridDefaultSortInfoRaw = useControlledSortFilter ? (initialSortInfo ?? props.defaultSortInfo) : props.defaultSortInfo;
  const gridDefaultSortInfo = (isRemoteGrid && effectiveMultiSort)
    ? gridDefaultSortInfoRaw
    : normalizeSingleSortInfo(gridDefaultSortInfoRaw);
  const gridDefaultFilterValue = useControlledSortFilter ? (initialFilterValue ?? props.defaultFilterValue) : props.defaultFilterValue;
  const gridSortInfoRaw = useControlledSortFilter ? sortInfo : props.sortInfo;
  const gridSortInfo = (isRemoteGrid && effectiveMultiSort)
    ? gridSortInfoRaw
    : normalizeSingleSortInfo(gridSortInfoRaw);
  const gridFilterValue = useControlledSortFilter ? filterValue : props.filterValue;
  const gridOnSortInfoChange = useControlledSortFilter ? onSortInfoChange : props.onSortInfoChange;
  const gridOnFilterValueChange = useControlledSortFilter ? onFilterValueChange : props.onFilterValueChange;
  const localPreferenceGridKey = hasPreference && !useControlledSortFilter
    ? `${props.preference || 'grid'}:${columnPreferenceSignature || preferenceSignature || 'default'}`
    : undefined;

  const loadReport = React.useCallback(
    async ({
             skip,
             limit,
             sortInfo: requestSortInfo,
             groupBy,
             filterValue: requestFilterValue,
           }) => {
      const effectiveSortInfo = useControlledSortFilter
        ? gridSortInfo
        : (requestSortInfo == null ? gridSortInfo : requestSortInfo);
      const effectiveFilterValue = useControlledSortFilter
        ? gridFilterValue
        : (requestFilterValue == null ? gridFilterValue : requestFilterValue);
      const state = reportState.current;
      const sequence = ++state.sequence;
      const fallback = () => reportState.current.good || {data: [], count: 0};
      if (filterConflict) return fallback();
      try {
        const response = await execute(
          {
            ...reportParams,
            skip,
            limit,
            sortInfo: effectiveSortInfo,
            groupBy,
            filterValue: effectiveFilterValue,
          },
          {
            urlPath: report,
          },
        );
        const result = parseReportResponse(response, prepareData, report);
        if (reportState.current !== state || state.sequence !== sequence) return fallback();
        state.good = result;
        setReportError(null);
        if (afterLoad) afterLoad(response);
        return result;
      } catch (error) {
        if (reportState.current === state && state.sequence === sequence) {
          setReportError({scope: reportScope, message: state.good
            ? 'Unable to refresh results. Previous results are shown; requested changes have not been applied.'
            : 'Unable to load results. Please try again.'});
        }
        return fallback();
      }
    },
    [afterLoad, execute, prepareData, report, reportParams, gridSortInfo, gridFilterValue, useControlledSortFilter,
      reportScope, filterConflict, retry],
  );

  const dataSource = React.useMemo(
    () => report ? loadReport : (prepareData ? prepareData(inputDataSource) || [] : inputDataSource),
    [inputDataSource, report, prepareData, loadReport],
  );
  const rowHeight = React.useMemo(
    () => props.rowHeight || (props.report ? 35 : 49),
    [props.report, props.rowHeight],
  );
  const headerHeight = React.useMemo(
    () => props.headerHeight || 42,
    [props.headerHeight],
  );
  const length = React.useMemo(
    () => dataSource?.length,
    [dataSource?.length],
  );
  const gridStyle = React.useMemo(
    () => ({
      minHeight: props.autoHeight ? (headerHeight + rowHeight + 10) : undefined,
      height: props.autoHeight ? (headerHeight + ((length === 0 ? 1 : length) * rowHeight) + 3) : undefined,
    }),
    [props.autoHeight, headerHeight, length, rowHeight],
  );

  return (
    <>
    {(filterConflict || reportError?.scope === reportScope) && (
      <div role="alert" className="alert alert-warning">
        {filterConflict || reportError.message}{' '}
        {!filterConflict && <button type="button" className="btn btn-sm btn-outline-light"
          disabled={loading} onClick={() => setRetry((value) => value + 1)}>Retry</button>}
      </div>
    )}
    <ReactDataGrid
      key={localPreferenceGridKey}
      idProperty="id"
      style={gridStyle}
      sortable={false}
      reorderColumns={false}
      showColumnMenuTool={false}
      theme="default-dark"
      rowHeight={rowHeight}
      headerHeight={headerHeight}
      resizable={isResizable}
      multiSort={effectiveMultiSort}
      allowUnsort={isRemoteGrid ? (props.allowUnsort ?? false) : false}
      remoteSort={isRemoteGrid}
      remoteFilter={isRemoteGrid}
      remotePagination={isRemoteGrid}
      scrollProps={{
        autoHide: !props.report,
      }}
      {...props}

      className={mergedClassName}

      defaultSortInfo={gridDefaultSortInfo}
      defaultFilterValue={gridDefaultFilterValue}
      sortInfo={gridSortInfo}
      columnOrder={props.preference ? columnOrder : props.columnOrder}
      filterValue={gridFilterValue}
      onSortInfoChange={gridOnSortInfoChange}
      onColumnResize={props.preference ? onColumnResize : props.onColumnResize}
      onColumnOrderChange={props.preference ? onColumnOrderChange : props.onColumnOrderChange}
      onFilterValueChange={gridOnFilterValueChange}
      onColumnVisibleChange={props.preference ? onColumnVisibleChange : props.onColumnVisibleChange}

      columns={columns}
      loading={props.report ? loading : (props.loading || false)}
      dataSource={dataSource}
    />
    </>
  )
});

export default Grid;
